import React, { useRef } from "react";
import { EditDialog, ExposedFunctions } from "../framework/editDialog";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { TextField, Checkbox, FormControlLabel } from "@mui/material";
import * as StoreActions from "../../models/store/storeActions";
import Grid2 from "@mui/material/Unstable_Grid2";
import { STYLE_CONSTANTS } from "../../framework/theme";
import { useFormik } from "formik";
import * as yup from "yup";
import { IApiError } from "../../services/baseService";
import { useTranslation } from "react-i18next";
import { reloadRoutes } from "../../store/workShiftPlanningSlice";
import { useAppDispatch } from "../../framework/customStore";
import { saveRoute } from "../../services/routeService";
import { RouteSaveDto } from "../../models/route/route";

interface RouteDialogProps {
    item: RouteSaveDto;
    open: boolean;
    onClose: () => void;
    onSaved?: (route: RouteSaveDto) => void;
    enableIsActive?: boolean;
}

export const RouteDialog = ({
    item,
    open,
    onClose,
    onSaved,
    enableIsActive = false,
}: RouteDialogProps) => {
    const dialogRef = useRef<ExposedFunctions>();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const handleSubmit = (formData: RouteSaveDto) => {
        const onSubmitSuccess = (savedRoute: RouteSaveDto) => {
            formik.setSubmitting(false);
            dialogRef.current.setInitialFormDataFromForm();
            dispatch(StoreActions.showSuccessMessage(t("common.saveSuccess")));
            dispatch(reloadRoutes());
            if (onSaved && savedRoute.id) onSaved(savedRoute);
            onClose();
        };

        const onSubmitFailure = (error: IApiError) => {
            dispatch(
                StoreActions.showErrorMessage(
                    error?.status === 409
                        ? t("common.saveConflict")
                        : t("common.saveFailed")
                )
            );
            formik.setSubmitting(false);
        };

        saveRoute(formData).then(onSubmitSuccess).catch(onSubmitFailure);
    };

    const formik = useFormik<RouteSaveDto>({
        ...defaultFormikConfig,
        enableReinitialize: true,
        initialValues: item,
        validationSchema: yup.object({
            name: yup
                .string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("common.name"),
                    })
                )
                .max(50),
        }),
        onSubmit: handleSubmit,
    });

    return (
        <EditDialog
            loading={!item}
            open={open}
            title={t("route.newRoute")}
            formik={formik}
            onClose={onClose}
            enableDefaultActions
            ref={dialogRef}
            body={
                <Grid2
                    container
                    direction="column"
                    spacing={STYLE_CONSTANTS.formSpacing}
                    sx={{ width: { xs: "100%", md: "auto" } }}
                >
                    <Grid2 xs={12}>
                        <TextField
                            label={t("common.name")}
                            fullWidth
                            required
                            {...formikFieldProps(formik, "name")}
                            value={formik.values?.name ?? ""}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <TextField
                            label={t("common.description")}
                            fullWidth
                            multiline
                            minRows={3}
                            {...formikFieldProps(formik, "comment")}
                            value={formik.values?.comment ?? ""}
                        />
                    </Grid2>
                    {enableIsActive ? (
                        <Grid2 xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            formik.values?.isActive ?? true
                                        }
                                        onChange={(e) =>
                                            void formik.setFieldValue(
                                                "isActive",
                                                e.target.checked
                                            )
                                        }
                                        color="success"
                                    />
                                }
                                label={t("common.inUse")}
                            />
                        </Grid2>
                    ) : null}
                </Grid2>
            }
        />
    );
};
